import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';
import { createSelector } from 'reselect';

import Api from '../api';

import { IRootState } from './index';
import Company from '../models/Company';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';

import PagedResultSet from '../models/common/PagedResultSet';

// Store
export interface ICompanyState {
    isLoadingCompanies: boolean;
    companies: PagedResultSet<Company> | undefined;
}

const initialState: ICompanyState = {
    isLoadingCompanies: false,
    companies: undefined,
};

// Action Types
export enum actionTypes {
    GET_COMPANIES = '[company] GET_COMPANIES',
    GET_COMPANIES_SUCCESS = '[company] GET_COMPANIES_SUCCESS',
    GET_COMPANY = '[company] GET_COMPANY',
    GET_COMPANY_SUCCESS = '[company] GET_COMPANY_SUCCESS',
}

// Action Creator
export const actionCreators = {
    getCompanies: () => {
        return { type: actionTypes.GET_COMPANIES };
    },
    getCompaniesSuccess: (companies: PagedResultSet<Company>) => {
        return { type: actionTypes.GET_COMPANIES_SUCCESS, payload: companies };
    },
    getCompany: (id: number) => {
        return { type: actionTypes.GET_COMPANY, payload: id };
    },
    getCompanySuccess: (company: Company) => {
        return { type: actionTypes.GET_COMPANY_SUCCESS, payload: company };
    },
};

// Reducer
const reducer: Reducer<ICompanyState> = (state: ICompanyState = initialState, action: Action): ICompanyState => {
    switch (action.type) {
        case actionTypes.GET_COMPANY:
            return {
                ...state,
                isLoadingCompanies: true,
            };
        case actionTypes.GET_COMPANIES:
            return {
                ...state,
                companies: initialState.companies,
                isLoadingCompanies: true,
            };
        case actionTypes.GET_COMPANY_SUCCESS:
            const getCompanyPayload = (action as { type: string; payload: Company }).payload;
            const companyResultSet = new PagedResultSet<Company>();
            companyResultSet.items.push(getCompanyPayload);
            return {
                ...state,
                companies: companyResultSet,
                isLoadingCompanies: false,
            };
        case actionTypes.GET_COMPANIES_SUCCESS:
            const getCompaniesPayload = (action as { type: string; payload: PagedResultSet<Company> }).payload;
            return {
                ...state,
                companies: getCompaniesPayload,
                isLoadingCompanies: false,
            };
        default:
            return state;
    }
};

export default reducer;

const companiesSelector = (state: IRootState): PagedResultSet<Company> | undefined => state.company.companies;
const companySelector = (id: number) =>
    createSelector(companiesSelector, (companies) => {
        return companies ? companies.items.filter((company: Company) => company.id === Number.parseInt(id + '', 10))[0] : undefined;
    });

export const selectors = {
    isLoadingCompanies: (state: IRootState): boolean => state.company.isLoadingCompanies || state.layout.initialLoading,
    companies: companiesSelector,
    company: companySelector,
};

// Side-Effects
const sagas = {
    getCompanies: function* (action: { type: string }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        // const tableConfig = yield select(selectors.clientsPageTableConfig);
        // const options = ODataOptions.from(tableConfig);
        const [result] = yield all([call(Api.Client.getAll, undefined, jwt), delay(200)]);
        yield put(actionCreators.getCompaniesSuccess(result));
    },
    getCompany: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        let client: Company;
        const clientId = action.payload;
        client = yield select(selectors.company(clientId));
        if (client) {
            yield put(actionCreators.getCompanySuccess(client));
        } else {
            const jwt = yield select(authSelectors.jwt);
            const [result] = yield all([call(Api.Client.get, clientId, jwt), delay(200)]);
            yield put(actionCreators.getCompanySuccess(result));
        }
    },
};

const watches = {
    getCompanies: function* () {
        yield takeEvery(actionTypes.GET_COMPANIES, sagas.getCompanies);
    },
    getCompany: function* () {
        yield takeEvery(actionTypes.GET_COMPANY, sagas.getCompany);
    },
};

export function* saga() {
    yield fork(watches.getCompanies);
    yield fork(watches.getCompany);
}



// WEBPACK FOOTER //
// ./src/reducers/Company.ts